import React, { useEffect, useRef, useState } from 'react';
import 'react-quill/dist/quill.snow.css';
import ReactQuill, { Quill } from 'react-quill-new';
import useLogic from './hooks/useLogic';
import SuggestionBackdrop from 'Global/Components/CommonComponents/SuggestionBackdrop/SuggestionBackdrop';
import { Button, Stack } from '@mui/material';
import {
  AutoFixHigh,
  MicRounded,
} from '@mui/icons-material';
import sanitize from 'sanitize-html';
import { themeColors } from 'Global/JsxThemeColor/JsxThemeColor';
import useHandleSpeechRecognitionOutput from 'Global/Hooks/useHandleSpeechRecognitionOutput/useHandleSpeechRecognitionOutput';

// Import and register the custom block format outside of the component
const Block = Quill.import('blots/block');
Block.tagName = 'p';
Quill.register(Block, true);

const EditableRichTextInput = ({
  onChange,
  field,
  enableSuggest,
  element,
  getHTML = false,
  width = null,
  placeholder = '',
}) => {
  const [UiValue, setUiValue] = useState('');

  const {
    value,
    setValue,
    setContent,
    backdrop,
    HandleOpenBackdrop,
    setBackdrop,
  } = useLogic(onChange, getHTML);

  const quillRef = useRef();
  const buttonRef = useRef(); // Create a ref for the button

  useEffect(() => {
    const htmlContent = sanitize(
      field?.Value || field?.value || ''
    );
    quillRef.current
      .getEditor()
      .clipboard.dangerouslyPasteHTML(htmlContent);

    // Set the Quill editor's width to match the button's width
    const resizeQuillToMatchButton = () => {
      const quillEditor =
        quillRef.current.getEditor().container;
      const buttonWidth = buttonRef.current.offsetWidth;
      quillEditor.style.width = `${buttonWidth}px`;
    };

    // Call the resize function after the component mounts
    resizeQuillToMatchButton();

    // Optional: If you want to ensure the Quill editor resizes with the window, add an event listener
    window.addEventListener(
      'resize',
      resizeQuillToMatchButton
    );

    // Cleanup the event listener on component unmount
    return () =>
      window.removeEventListener(
        'resize',
        resizeQuillToMatchButton
      );
  }, [field]);

  const [
    transcript,
    isListening,
    startListening,
    stopListening,
  ] = useHandleSpeechRecognitionOutput(UiValue, setUiValue);

  useEffect(() => {
    if (UiValue) {
      quillRef.current.getEditor().setText(UiValue);
    }
  }, [UiValue]);

  return (
    <React.Fragment>
      <div style={{ marginTop: '.5em' }}></div>
      <SuggestionBackdrop
        openBackdrop={backdrop}
        setUiValue={(value) => {
          quillRef.current.getEditor().setText(value);
        }}
        element={element}
        setSuggestion={setBackdrop}
        onChange={onChange}
      />
      <Stack position="relative" ref={buttonRef}>
        <ReactQuill
          placeholder={placeholder}
          theme="snow"
          value={value}
          ref={quillRef}
          onChange={(content, delta, source, editor) => {
            const text = editor.getText(content);

            if (text.trim().length === 0) {
              setUiValue('');
            }
            setValue(content);
            setContent(text);
          }}
        />
        <Stack direction="row" gap=".5em" width="100%">
          <Button
            endIcon={
              <MicRounded
                color="primary"
                sx={{
                  fill: isListening
                    ? 'red !important'
                    : `${themeColors.ColorPrimary} !important`,
                  transition: 'all .5s ease',
                }}
              />
            }
            onClick={() => {
              if (isListening) {
                stopListening();
              } else {
                startListening();
              }
            }}
            variant="contained"
            color="colorPrimer"
            sx={{
              color: `${themeColors.ColorPrimary} !important`,
              mb: '.3em',
            }}
            fullWidth
          />
          <Button
            endIcon={
              <AutoFixHigh
                color="primary"
                sx={{
                  color: `${themeColors.ColorPrimary} !important`,
                }}
              />
            }
            onClick={HandleOpenBackdrop}
            variant="contained"
            color="colorPrimer"
            sx={{
              color: `${themeColors.ColorPrimary} !important`,
              mb: '.3em',
            }}
            fullWidth
          />
        </Stack>
      </Stack>
    </React.Fragment>
  );
};

export default EditableRichTextInput;
